/**
 * The Page component
 */
import React from "react";
import Helmet from "react-helmet";
import {
  REACT_APP_WEB_SERVER,
  REACT_APP_CDN_SERVER,
  REACT_APP_FB_ID,
} from "../../utils/env";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";

/** Default values constants */
const defaultTitle = "myfolie.com";
const defaultKeywords =
  "Klebefolien, Autobeschriftung, Fahrzeugbeschriftung, Schaufenster";
const defaultDescription =
  "Jetzt Deine Klebeschriften und Klebebuchstaben online erstellen. ✓individuell ✓ viele Schriftarten ✓ viele Farben ✓ auf Rechnung";
const defaultImage = `${REACT_APP_CDN_SERVER}/media/banner/800x500.png`;
const defaultTwitter = "@myfolie";
const defaultSep = " | ";

/**
 * Page Component
 * - sets SEO data for each page
 * - used to wrap routes
 */
const getMetaTags = (
  {
    title,
    description,
    image,
    contentType,
    twitter,
    noCrawl,
    published,
    updated,
    category,
    keywords,
    tags,
  },
  pathname
) => {
  const theTitle = title
    ? (title + defaultSep + defaultTitle).substring(0, 75)
    : defaultTitle;
  const theDescription = description
    ? description
        .replace(/<[^>]*>/g, "")
        .replace(/(\r\n|\n|\r)/gm, " ")
        .substring(0, 160)
    : defaultDescription;
  const theImage = image ? image : defaultImage;
  const theKeywords = keywords ? keywords : defaultKeywords;

  const metaTags = [
    { itemprop: "name", content: theTitle },
    { itemprop: "description", content: theDescription },
    { itemprop: "keywords", content: theKeywords },
    { name: "keywords", content: theKeywords },
    { itemprop: "image", content: theImage },
    { name: "description", content: theDescription },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:site", content: defaultTwitter },
    { name: "twitter:title", content: theTitle },
    { name: "twitter:description", content: theDescription },
    { name: "twitter:creator", content: twitter || defaultTwitter },
    { name: "twitter:image:src", content: theImage },
    { property: "og:title", content: theTitle },
    { property: "og:type", content: contentType || "website" },
    { property: "og:url", content: REACT_APP_WEB_SERVER + pathname },
    { property: "og:image", content: theImage },
    { property: "og:description", content: theDescription },
    { property: "og:site_name", content: defaultTitle },
    { property: "fb:app_id", content: REACT_APP_FB_ID },
  ];

  if (noCrawl) {
    metaTags.push({ name: "robots", content: "noindex, nofollow" });
  }
  if (published) {
    metaTags.push({ name: "article:published_time", content: published });
  }
  if (updated) {
    metaTags.push({ name: "article:modified_time", content: updated });
  }
  if (category) {
    metaTags.push({ name: "article:section", content: category });
  }
  if (tags) {
    metaTags.push({ name: "article:tag", content: tags });
  }
  metaTags.push({
    name: "msapplication-config",
    content: `${REACT_APP_CDN_SERVER}/favicon/browserconfig.xml`,
  });
  return metaTags;
};

/** generate Links */
const getLinkTags = ({ canonical_url }, pathname) => {
  // FIXME not production ready! always using hardcoded urls
  const nolang_pathname = pathname.substr(4);
  // IF canonical is given but it does not start with a language it should be a relativ url.
  // IF it starts with language we add the site url.
  // IF it starts with site url it is a absolute url for all languages
  let normalized_canonical_url = REACT_APP_WEB_SERVER + pathname;
  if (canonical_url) {
    if (canonical_url.startsWith(REACT_APP_WEB_SERVER)) {
      normalized_canonical_url = canonical_url;
    } else if (
      !["/de/", "/en/", "/it/", "/es/"].includes(canonical_url.substr(0, 4))
    ) {
      normalized_canonical_url =
        REACT_APP_WEB_SERVER + pathname.substr(0, 4) + canonical_url.substr(4);
    } else {
      // does not start with https but starts with language
      normalized_canonical_url = REACT_APP_WEB_SERVER + canonical_url;
    }
  }
  const linkTags = [
    { rel: "canonical", href: normalized_canonical_url },
    {
      rel: "alternate",
      hreflang: "de",
      href: REACT_APP_WEB_SERVER + "/de/" + nolang_pathname,
    },
    {
      rel: "alternate",
      hreflang: "en",
      href: REACT_APP_WEB_SERVER + "/en/" + nolang_pathname,
    },
    {
      rel: "alternate",
      hreflang: "es",
      href: REACT_APP_WEB_SERVER + "/es/" + nolang_pathname,
    },
    {
      rel: "alternate",
      hreflang: "it",
      href: REACT_APP_WEB_SERVER + "/it/" + nolang_pathname,
    },
    {
      rel: "alternate",
      hreflang: "x-default",
      href: REACT_APP_WEB_SERVER + "/de/" + nolang_pathname,
    },
  ];
  return linkTags;
};
/**
 * Helper function to generate JSON-LD data
 */
const getJsonLDdata = (
  { title, description, keywords, pageJsonLd },
  pathname
) => {
  // Return the predefined JSON-LD if available
  if (pageJsonLd) return pageJsonLd;

  const theTitle = title
    ? (title + defaultSep + defaultTitle).substring(0, 75)
    : defaultTitle;
  const theDescription = description
    ? description
        .replace(/<[^>]*>/g, "")
        .replace(/(\r\n|\n|\r)/gm, " ")
        .substring(0, 160)
    : defaultDescription;
  const theKeywords = keywords || defaultKeywords;

  // Create a base structure for the JSON-LD
  const baseJsonLd = {
    "@context": "http://schema.org",
    "@type": "WebSite",
    url: REACT_APP_WEB_SERVER + encodeURIComponent(pathname),
    name: theTitle,
    description: theDescription,
    keywords: theKeywords,
    publisher: {
      "@type": "Organization",
      name: "myfolie GmbH",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      bestRating: 5,
      worstRating: 1,
      ratingValue: 4.9,
      reviewCount: 1420,
    },
  };

  // Product-specific data configuration
  const productConfig = {
    "/de/klebeschriften/": {
      "@type": "Product",
      name: "Beschriftung aus Klebefolie",
      description:
        "Gestalte deine individuelle Beschriftung für Auto und Fenster in vielen Farben und Schriften geschnitten aus hochwertigen Vinyl Klebefolien.",
      image: "https://cdn.myfolie.de/media/products/foto-klebeschrift.jpg",
      offers: {
        "@type": "Offer",
        priceCurrency: "EUR",
        price: "4.95",
        availability: "https://schema.org/InStock",
        seller: {
          "@type": "Organization",
          name: "myfolie GmbH",
        },
      },
    },
    "/de/folienzuschnitt/": {
      "@type": "Product",
      name: "Individueller Zuschnitt von Klebefolie in Wunschgröße",
      description:
        "Wähle aus über 300 verschiedenen Folien deine Wunschfarbe und Material und wir schneiden sie dir in Wunschgröße zu. Schnell und günstig!",
      offers: {
        "@type": "Offer",
        priceCurrency: "EUR",
        price: "11.50",
        availability: "https://schema.org/InStock",
        seller: {
          "@type": "Organization",
          name: "myfolie GmbH",
        },
      },
    },
    "/de/eigenes-motiv/": {
      "@type": "Product",
      name: "Klebefolie mit Digitaldruck (Aufkleber)",
      description:
        "Dein Motiv im Digitaldruck auf hochwertiger Klebefolie in verschiedenen Schnitten. Ideal auch als Autoaufkleber oder als Aufkleber im Schaufenster.",
      offers: {
        "@type": "Offer",
        priceCurrency: "EUR",
        price: "3.79",
        availability: "https://schema.org/InStock",
        seller: {
          "@type": "Organization",
          name: "myfolie GmbH",
        },
      },
    },
  };

  // Check if the pathname matches a specific product configuration
  if (productConfig[pathname]) {
    return {
      ...baseJsonLd,
      ...productConfig[pathname],
      url: REACT_APP_WEB_SERVER + pathname,
      aggregateRating: baseJsonLd.aggregateRating,
      brand: {
        "@type": "Organization",
        name: "myfolie GmbH",
      },
      potentialAction: {
        "@type": "SearchAction",
        target: `https://www.myfolie.com${pathname.substring(
          0,
          4
        )}search/?q={query}`,
        "query-input": "required name=query",
      },
    };
  }

  // If no specific product configuration matches, return the base JSON-LD
  return baseJsonLd;
};

const getScriptTags = ({ dataLayer, pageType, jsonLd, ...rest }, pathname) => {
  let scripts = [];
  // add JsonLDData
  scripts.push({
    type: "application/ld+json",
    innerHTML: jsonLd ? jsonLd : JSON.stringify(getJsonLDdata(rest, pathname)),
  });

  scripts.push({
    innerHTML: `
      ${getDataLayerObject({ dataLayer, pageType }, pathname)}
    `,
  });

  return scripts;
};

/**
 * Helper function to build the Data Layer Object
 */
const getDataLayerObject = ({ dataLayer, pageType }, pathname) => {
  if (
    (pageType === "product" ||
      pageType === "landingpage" ||
      pageType === "catgory") &&
    !dataLayer
  )
    return ""; // Only Return for Products if Datalayer is set as it indicates product is loaded

  const pageTypeValue = pageType || "unknown";
  const dataLayerString =
    dataLayer && dataLayer?.error !== "undefined"
      ? Array.isArray(dataLayer)
        ? dataLayer
            .map((entry) => `window.dataLayer.push(${JSON.stringify(entry)});`)
            .join("\n")
        : `window.dataLayer.push(${JSON.stringify(dataLayer)});`
      : "";

  return `
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    ${dataLayerString}
    window.dataLayer.push({"pageType": "${pageTypeValue}"});
    window.dataLayer.push({"currentPagePath": "${pathname}" });
    window.dataLayer.push({"event": "custom.pageLoaded"});
  `;
};

const Page = ({ id, className, children, ...props }) => {
  const location = useLocation();
  const metatags = getMetaTags(props, location.pathname);
  const links = getLinkTags(props, location.pathname);
  const scripts = getScriptTags(props, location.pathname);

  return (
    <div id={id} className={className}>
      <Helmet
        htmlAttributes={{
          lang: props.lang || "en",
          itemscope: undefined,
          itemtype: `http://schema.org/${props.schema || "WebPage"}`,
        }}
        title={
          props.title
            ? props.title.substring(0, 80) + defaultSep + defaultTitle
            : defaultTitle
        }
        link={links}
        meta={metatags}
        script={scripts}
      ></Helmet>
      {children}
    </div>
  );
};

// PropTypes validation
Page.propTypes = {};

export default Page;
